import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', full_name: '', phone: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('token', response.data.access_token);
      onLogin(response.data.user);
      toast.success('Connexion réussie!');
      
      if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Email ou mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      localStorage.setItem('token', response.data.access_token);
      onLogin(response.data.user);
      toast.success('Compte créé avec succès!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen pt-20 pb-24 px-6 md:px-12 lg:px-24 flex items-center justify-center grain-texture">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-serif tracking-tight mb-4">Espace Client</h1>
          <p className="text-base leading-relaxed text-muted-foreground">
            Accédez à votre espace personnel
          </p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger data-testid="login-tab" value="login">Connexion</TabsTrigger>
            <TabsTrigger data-testid="register-tab" value="register">Créer un compte</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} data-testid="login-form" className="bg-white border border-border p-8 space-y-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Email</label>
                <Input
                  data-testid="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  required
                  className="border-bottom-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Mot de passe</label>
                <Input
                  data-testid="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  required
                  className="border-bottom-input"
                />
              </div>

              <Button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full btn-primary bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} data-testid="register-form" className="bg-white border border-border p-8 space-y-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Nom complet</label>
                <Input
                  data-testid="register-name"
                  type="text"
                  value={registerData.full_name}
                  onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                  required
                  className="border-bottom-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Email</label>
                <Input
                  data-testid="register-email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  required
                  className="border-bottom-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Téléphone</label>
                <Input
                  data-testid="register-phone"
                  type="tel"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                  required
                  className="border-bottom-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 block">Mot de passe</label>
                <Input
                  data-testid="register-password"
                  type="password"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                  required
                  className="border-bottom-input"
                />
              </div>

              <Button
                data-testid="register-submit"
                type="submit"
                disabled={loading}
                className="w-full btn-primary bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-sm uppercase tracking-widest font-semibold"
              >
                {loading ? 'Création...' : 'Créer mon compte'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;